import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Popover, Typography, useTheme } from '@mui/material';
import { IconPhone } from '@tabler/icons-react';

import { BrandIcon } from 'src/components/brand-icon/BrandIcon';
import { useGetBrandsQuery } from 'src/services/brand/brandApi';
import { Brand } from 'src/types/brandTypes';

interface VoiceBrandBadgeProps {
  height?: string;
  phoneNumbers: string[];
}

interface BrandWithPhones {
  brand: Brand;
  brandIndex: number;
  phoneNumbers: string[];
}

export const VoiceBrandBadge = ({
  height,
  phoneNumbers,
}: VoiceBrandBadgeProps) => {
  const { palette } = useTheme();
  const { t } = useTranslation('workflow-builder');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { data: brands } = useGetBrandsQuery({ brandType: 'solve-voice' });

  const brandsWithPhones = useMemo(() => {
    if (!brands || !phoneNumbers || phoneNumbers.length === 0) {
      return [];
    }

    // Create a map of phone numbers to brands
    const brandMap = new Map<string, BrandWithPhones>();

    phoneNumbers.forEach(phoneNumber => {
      const foundBrand = brands.find(brand => brand.tags.includes(phoneNumber));
      if (foundBrand) {
        const brandId = foundBrand.brand_id;
        if (brandMap.has(brandId)) {
          const existingBrandWithPhones = brandMap.get(brandId);
          if (existingBrandWithPhones) {
            existingBrandWithPhones.phoneNumbers.push(phoneNumber);
          }
        } else {
          const brandIndex = brands.findIndex(
            b => b.brand_id === foundBrand.brand_id,
          );
          brandMap.set(brandId, {
            brand: foundBrand,
            brandIndex,
            phoneNumbers: [phoneNumber],
          });
        }
      }
    });

    return Array.from(brandMap.values());
  }, [brands, phoneNumbers]);

  const firstBrand = brandsWithPhones[0];
  const additionalBrandsCount = brandsWithPhones.length - 1;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (!firstBrand) {
    return null;
  }

  const badgeLabel =
    additionalBrandsCount > 0
      ? t('voiceBrandBadge.moreBrands', {
          brandName: firstBrand.brand.brand_name,
          count: additionalBrandsCount,
        })
      : firstBrand.brand.brand_name;

  return (
    <>
      <Box
        alignItems='center'
        borderRadius='4px'
        boxSizing='border-box'
        component='div'
        display='flex'
        gap={0.5}
        height={height || '24px'}
        maxWidth='100%'
        onClick={handleClick}
        overflow='hidden'
        padding='4px 8px'
        sx={{
          ':hover': {
            bgcolor: theme => theme.palette.colors.slate[200],
            cursor: 'pointer',
          },
          bgcolor: theme => theme.palette.colors.slate[100],
        }}
        width='fit-content'
      >
        <BrandIcon index={firstBrand.brandIndex} variant='small' />
        <Typography
          noWrap
          sx={{ color: theme => theme.palette.colors.grey[700] }}
          variant='font12Medium'
        >
          {badgeLabel}
        </Typography>
      </Box>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            sx: {
              border: theme => `1px solid ${theme.palette.colors.slate[200]}`,
              borderRadius: '4px',
              boxShadow: theme => theme.shadows[1],
              mt: 0.5,
              px: 2,
              py: 1.5,
            },
          },
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
      >
        <Box display='flex' flexDirection='column' gap={2} minWidth='280px'>
          <Typography
            sx={{ color: theme => theme.palette.colors.grey[500] }}
            variant='font14Medium'
          >
            {t('voiceBrandBadge.brands')}
          </Typography>
          {brandsWithPhones.map(({ brand, brandIndex, phoneNumbers }) => (
            <Box
              display='flex'
              flexDirection='column'
              gap={2}
              key={brand.brand_id}
            >
              <Box alignItems='center' display='flex' gap={0.5}>
                <BrandIcon index={brandIndex} variant='small' />
                <Typography
                  sx={{ color: theme => theme.palette.colors.grey[900] }}
                  variant='font14Medium'
                >
                  {brand.brand_name}
                </Typography>
              </Box>
              {phoneNumbers.map(phoneNumber => (
                <Box
                  alignItems='center'
                  display='flex'
                  gap='4px'
                  key={phoneNumber}
                  ml={3}
                >
                  <IconPhone color={palette.colors.blue[500]} size={16} />
                  <Typography variant='font14'>{phoneNumber}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
